import { ComponentProps, FC, ReactNode } from 'react'
import cn from 'classnames'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import type { CrmLeadTelephonyEndpoint } from '@/api/types/crm/crmLeadFunnels.types'
import CrmProviderChatButton from '../../CrmProviderChatButton/CrmProviderChatButton'
import classes from '../index.module.css'
import TelephonyIconSlots from './TelephonyIconSlots'

type ProviderButtonConfig = {
  icon: ComponentProps<typeof CrmProviderChatButton>['icon']
  iconColor: string
  label: string
  enabled: boolean
  disabledTooltip: string | null
  enabledTooltip: string | null
  unreadLabel: string | null
  unreadCount: number
  menuAccounts: ComponentProps<typeof CrmProviderChatButton>['menuAccounts']
  accessDeniedTooltip: string
  onOpenChat?: (options?: { conversationId?: number }) => void
}

type ProviderIconsStackProps = {
  badgeSize: 'table' | 'card' | 'kanban'
  useStackedIconLook: boolean
  tooltipProps?: ComponentProps<typeof TooltipWrapper>
  showTelephony: boolean
  telephonyEndpoints: CrmLeadTelephonyEndpoint[]
  phoneNumber?: string
  onPickTelephonyEndpoint: (endpoint: CrmLeadTelephonyEndpoint) => void
  buttons: {
    vk: ProviderButtonConfig | null
    max: ProviderButtonConfig | null
    telegram: ProviderButtonConfig | null
    wazzup: ProviderButtonConfig | null
  }
}

const wrapSlot = (
  key: string,
  unreadCount: number,
  node: ReactNode,
): ReactNode => (
  <span
    key={key}
    className={cn(
      classes.iconSlot,
      Number(unreadCount) > 0 && classes.iconSlotWithBadge,
    )}
  >
    {node}
  </span>
)

const ProviderIconsStack: FC<ProviderIconsStackProps> = ({
  badgeSize,
  useStackedIconLook,
  tooltipProps,
  showTelephony,
  telephonyEndpoints,
  phoneNumber,
  onPickTelephonyEndpoint,
  buttons,
}) => {
  const telephonySlots = showTelephony ? (
    <TelephonyIconSlots
      endpoints={telephonyEndpoints}
      phoneNumber={phoneNumber}
      badgeSize={badgeSize}
      useStackedIconLook={useStackedIconLook}
      tooltipProps={tooltipProps}
      onPickEndpoint={onPickTelephonyEndpoint}
    />
  ) : null

  const providerSlots = (
    [
      ['vk', buttons.vk],
      ['max', buttons.max],
      ['telegram', buttons.telegram],
      ['wazzup', buttons.wazzup],
    ] as const
  )
    .map(([key, config]) => {
      if (!config) return null
      return wrapSlot(
        key,
        config.unreadCount,
        <CrmProviderChatButton
          icon={config.icon}
          iconColor={config.iconColor}
          label={config.label}
          enabled={config.enabled}
          disabledTooltip={config.disabledTooltip}
          enabledTooltip={config.enabledTooltip}
          unreadLabel={config.unreadLabel}
          badgeSize={badgeSize}
          menuAccounts={config.menuAccounts}
          accessDeniedTooltip={config.accessDeniedTooltip}
          tooltipProps={tooltipProps}
          onOpenChat={config.onOpenChat}
        />,
      )
    })
    .filter(Boolean)

  const slots = [telephonySlots, ...providerSlots].filter(Boolean)
  if (slots.length === 0) return null

  const slotCount =
    (showTelephony ? telephonyEndpoints.length : 0) + providerSlots.length

  return (
    <div
      className={cn(classes.providerIconsStack, {
        [classes.providerIconsStackSingle]: slotCount === 1,
      })}
    >
      {slots}
    </div>
  )
}

export default ProviderIconsStack
